import Router from 'next/router'
import { useSyncExternalStore } from 'react'
import { Client, CombinedError } from 'urql'

import matcher from '@/partner-gql/generated/matcher'

import { PARTNER_GRAPHQL_ENDPOINT } from './env'
import { graphql } from './partner-gql'
import {
  Authentication,
  clearAuthentication,
  getAuthentication,
  makeClientStore,
  saveAuthentication,
  withKeylessTypes,
} from './urql-common'

function isPresent(auth: Authentication | null): auth is Authentication {
  return auth != null
}

const PartnerTokenRefreshMutation = graphql(`
  mutation PartnerTokenRefreshMutation($fingerprint: String!, $refreshToken: ID!) {
    tokenRefresh(input: { fingerprint: $fingerprint, refreshToken: $refreshToken }) {
      authentication {
        accessToken
        fingerprint
        refreshToken
      }
    }
  }
`)

export function isContractSignRequiredError(error: unknown): boolean {
  if (!(error instanceof CombinedError)) return false
  return error.graphQLErrors.some((e) => e.extensions.code == 'CONTRACT_SIGN_REQUIRED_ERROR')
}

export function isGuardianTokenError(error: unknown): boolean {
  if (!(error instanceof CombinedError)) return false
  return error.graphQLErrors.some((e) => e.extensions.code == 'GUARDIAN_TOKEN_ON_OTHER_SCHEMA')
}

export function isSchoolTokenError(error: unknown): boolean {
  if (!(error instanceof CombinedError)) return false
  return error.graphQLErrors.some((e) => e.extensions.code == 'SCHOOL_TOKEN_ON_OTHER_SCHEMA')
}

const { getSnapshot, resetClient, subscribe } = makeClientStore(PARTNER_GRAPHQL_ENDPOINT, {
  authExchangeOptions: {
    anonymousFields: new Set([
      'signIn',
      'tokenRefresh',
      'latestContracts',
      'latestTermOfSaleContract',
      'latestCookieRegulationContract',
      'passwordForgot',
      'passwordForgotConfirm',
      '__schema',
    ]),
    refreshAuth: async (mutate) => {
      let authentication = getAuthentication()
      if (authentication) {
        const result = await mutate(PartnerTokenRefreshMutation, {
          fingerprint: authentication.fingerprint,
          refreshToken: authentication.refreshToken,
        })
        saveAuthentication(result.data?.tokenRefresh?.authentication ?? null)
        authentication = getAuthentication()
      }

      // refresh token successed
      if (isPresent(authentication)) return

      clearAuthentication()
      await logout()
    },
  },
  cacheExchangeOptions: {
    possibleTypes: matcher.possibleTypes,
    keys: withKeylessTypes(['Viewer']),
  },
  isErrorHandledInBoundary: (error) =>
    isContractSignRequiredError(error) || isGuardianTokenError(error) || isSchoolTokenError(error),
})

export async function logout() {
  clearAuthentication()

  await Router.push('/web/sign_in')
  resetClient()
}

export function resetUrqlClient() {
  resetClient()
}

export function useUrqlClient(): Client {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

export function getCurrentClient(): Client {
  return getSnapshot()
}
